import React, { FC, useContext, useMemo } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import ArrowIcon from '@ad-scripts/presentation/components/common/icons/stroke/down-arrow-and-circle-stroke';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';

type NodeSelectorType = {
  'data-aid': string;
  attr: string;
  label: string;
  omittedNodeIds?: string[];
  onChange?: Function;
  onSelectNode: Function;
  options: any[];
  otherProps?: any;
};

export const ClickableLabel = ({ 'data-aid': dataAid, disabled, label, onClick }) => {
  return (
    <StyledLabel data-aid={dataAid} onClick={onClick} disabled={disabled}>
      {label} {!disabled && <StyledArrowIcon />}
    </StyledLabel>
  );
};

const BaseNodeSelector: FC<NodeSelectorType & any> = ({
  'data-aid': dataAid,
  attr,
  label,
  omittedNodeIds,
  onChange,
  onSelectNode,
  options,
  ...otherProps
}) => {
  const { workingState } = useContext(FormContext);
  const dropdownValue = get(workingState, attr);
  const onNextNodeClick = () => dropdownValue && onSelectNode(dropdownValue);

  const nodeList = useMemo(() => {
    if (isEmpty(omittedNodeIds)) {
      return options;
    }
    return filter(options, (opt = {}) => !includes(omittedNodeIds, opt.value));
  }, [omittedNodeIds, options]);

  const dropdownLabel = (
    <ClickableLabel data-aid={`${dataAid}-label`} label={label} onClick={onNextNodeClick} disabled={!dropdownValue} />
  );

  return (
    <Dropdown
      allowEmptyValues
      attr={attr}
      data-aid={dataAid}
      label={dropdownLabel}
      onChange={onChange}
      options={nodeList}
      placeholder={dropdownValue ? 'Deleted Node' : 'None'}
      {...otherProps}
    />
  );
};

const StyledLabel = styled.div`
  align-items: center;
  display: flex;

  &:hover {
    ${p =>
      p.disabled
        ? 'cursor: default'
        : `
      color: ${p.theme.colors.green400};
      cursor: pointer;
  
      svg {
        fill: ${p.theme.colors.green400};
    `};
  }
`;

const StyledArrowIcon = styled(ArrowIcon)`
  margin-left: 5px;
  transform: rotate(270deg);
  width: 14px;
  height: 14px;
`;

export default BaseNodeSelector;
